import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Eye, Gift, Pencil, Plus, Trash2, Users } from "lucide-react";

const stageBadgeClass = (stageKey) => {
  if (stageKey === "consultancy_closed") return "bg-emerald-100 text-emerald-800";
  if (stageKey === "consultancy_in_progress" || stageKey === "consultancy_requested") return "bg-sky-100 text-sky-800";
  if (stageKey === "diagnostic_completed" || stageKey === "payment_confirmed") return "bg-blue-100 text-blue-800";
  if (stageKey === "awaiting_payment") return "bg-amber-100 text-amber-800";
  if (stageKey === "consultancy_not_closed") return "bg-rose-100 text-rose-800";
  return "bg-slate-100 text-slate-700";
};

const emptyCreate = {
  name: "",
  email: "",
  password: "",
  company_name: "",
  phone: "",
  crc: "",
  notes: "",
};

const AdminAccountants = () => {
  const [accountants, setAccountants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [showView, setShowView] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(false);
  const [selectedAccountant, setSelectedAccountant] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [newAccountant, setNewAccountant] = useState(emptyCreate);
  const [editData, setEditData] = useState({ company_name: "", phone: "", crc: "", notes: "", active: true });
  const [grantData, setGrantData] = useState({ discount_percent: "100", quantity: "1", notes: "" });

  const fetchAccountants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/accountants`, { withCredentials: true });
      setAccountants(response.data?.accountants || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar contadores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccountants();
  }, [fetchAccountants]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/accountants`, newAccountant, { withCredentials: true });
      toast.success("Contador cadastrado com sucesso");
      setShowCreate(false);
      setNewAccountant(emptyCreate);
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar contador");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (accountant) => {
    setSelectedAccountant(accountant);
    setEditData({
      company_name: accountant.company_name || "",
      phone: accountant.phone || "",
      crc: accountant.crc || "",
      notes: accountant.notes || "",
      active: accountant.active !== false,
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedAccountant) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/accountants/${selectedAccountant.accountant_id}`, editData, { withCredentials: true });
      toast.success("Contador atualizado");
      setShowEdit(false);
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar contador");
    } finally {
      setSaving(false);
    }
  };

  const openGrant = (accountant) => {
    setSelectedAccountant(accountant);
    setGrantData({ discount_percent: "100", quantity: "1", notes: "" });
    setShowGrant(true);
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!selectedAccountant) return;
    setGranting(true);
    try {
      await axios.post(
        `${API}/admin/accountants/${selectedAccountant.accountant_id}/grant-access`,
        { ...grantData, discount_percent: Number(grantData.discount_percent), quantity: Number(grantData.quantity) },
        { withCredentials: true }
      );
      toast.success("Acessos extras liberados");
      setShowGrant(false);
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao liberar acessos");
    } finally {
      setGranting(false);
    }
  };

  const openView = async (accountant) => {
    setSelectedAccountant(accountant);
    setShowView(true);
    try {
      const response = await axios.get(`${API}/admin/accountants/${accountant.accountant_id}/dashboard`, { withCredentials: true });
      setDashboardData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar detalhes do contador");
    }
  };

  const handleDelete = async (accountant) => {
    if (!window.confirm(`Deseja excluir o contador ${accountant.user_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/accountants/${accountant.accountant_id}`, { withCredentials: true });
      toast.success("Contador excluído");
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir contador");
    }
  };

  const cancelLink = async (link) => {
    if (!selectedAccountant) return;
    try {
      await axios.delete(`${API}/admin/accountants/${selectedAccountant.accountant_id}/links/${link.partnership_link_id}`, { withCredentials: true });
      toast.success("Acesso cancelado");
      openView(selectedAccountant);
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar acesso");
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-serif text-3xl text-navy">Contadores Parceiros</h1>
            <p className="text-slate-600">Gerencie cotas gratuitas, descontos e o desempenho dos parceiros contábeis.</p>
          </div>
          <Button className="bg-navy hover:bg-navy/90" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo contador
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Total</p><p className="mt-2 text-3xl font-bold text-navy">{accountants.length}</p></CardContent></Card>
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Ativos</p><p className="mt-2 text-3xl font-bold text-emerald-600">{accountants.filter((item) => item.active).length}</p></CardContent></Card>
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Diagnósticos</p><p className="mt-2 text-3xl font-bold text-sky-600">{accountants.reduce((sum, item) => sum + Number(item.diagnostics_count || 0), 0)}</p></CardContent></Card>
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Concluídos</p><p className="mt-2 text-3xl font-bold text-amber-600">{accountants.reduce((sum, item) => sum + Number(item.submitted_count || 0), 0)}</p></CardContent></Card>
        </div>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Lista de contadores</CardTitle>
            <CardDescription>Acompanhe o consumo dos acessos e a evolução dos diagnósticos vinculados.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
              </div>
            ) : (
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-3 pr-3">Contador</th>
                    <th className="py-3 pr-3">Escritório</th>
                    <th className="py-3 pr-3">Cotas</th>
                    <th className="py-3 pr-3">Diagnósticos</th>
                    <th className="py-3 pr-3">Média</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {accountants.map((accountant) => (
                    <tr key={accountant.accountant_id} className="border-b align-top">
                      <td className="py-3 pr-3">
                        <div className="font-semibold text-navy">{accountant.user_name}</div>
                        <div className="text-xs text-slate-500">{accountant.user_email}</div>
                      </td>
                      <td className="py-3 pr-3 text-slate-600">
                        <div>{accountant.company_name || "-"}</div>
                        <div className="text-xs">{accountant.crc || "Sem CRC"}</div>
                      </td>
                      <td className="py-3 pr-3 text-slate-600">
                        <div>100%: {accountant.quota_summary?.free_used || 0} / {accountant.quota_summary?.free_total || 0}</div>
                        <div>50%: {accountant.quota_summary?.half_used || 0} / {accountant.quota_summary?.half_total || 0}</div>
                        <div>Ativos: {accountant.quota_summary?.active_links || 0}</div>
                      </td>
                      <td className="py-3 pr-3 text-slate-600">
                        <div>Total: {accountant.diagnostics_count || 0}</div>
                        <div>Concluídos: {accountant.submitted_count || 0}</div>
                      </td>
                      <td className="py-3 pr-3 text-slate-600">{accountant.avg_score ? Number(accountant.avg_score).toFixed(2) : "-"}</td>
                      <td className="py-3 pr-3">
                        <Badge className={accountant.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                          {accountant.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openView(accountant)}>
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openGrant(accountant)}>
                            <Gift className="h-4 w-4 mr-1" />
                            Liberar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(accountant)}>
                            <Pencil className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(accountant)}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Novo contador parceiro</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Nome</Label><Input value={newAccountant.name} onChange={(e) => setNewAccountant({ ...newAccountant, name: e.target.value })} required /></div>
                <div><Label>E-mail</Label><Input type="email" value={newAccountant.email} onChange={(e) => setNewAccountant({ ...newAccountant, email: e.target.value })} required /></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Senha</Label><Input type="password" minLength={6} value={newAccountant.password} onChange={(e) => setNewAccountant({ ...newAccountant, password: e.target.value })} required /></div>
                <div><Label>Escritório</Label><Input value={newAccountant.company_name} onChange={(e) => setNewAccountant({ ...newAccountant, company_name: e.target.value })} /></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Telefone</Label><Input value={newAccountant.phone} onChange={(e) => setNewAccountant({ ...newAccountant, phone: e.target.value })} /></div>
                <div><Label>CRC</Label><Input value={newAccountant.crc} onChange={(e) => setNewAccountant({ ...newAccountant, crc: e.target.value })} /></div>
              </div>
              <div><Label>Observações</Label><Textarea value={newAccountant.notes} onChange={(e) => setNewAccountant({ ...newAccountant, notes: e.target.value })} /></div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy/90" disabled={creating}>
                <Users className="h-4 w-4 mr-2" />
                {creating ? "Cadastrando..." : "Cadastrar contador"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Editar contador parceiro</DialogTitle></DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Escritório</Label><Input value={editData.company_name} onChange={(e) => setEditData({ ...editData, company_name: e.target.value })} /></div>
                <div><Label>Telefone</Label><Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} /></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>CRC</Label><Input value={editData.crc} onChange={(e) => setEditData({ ...editData, crc: e.target.value })} /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={editData.active ? "active" : "inactive"} onValueChange={(value) => setEditData({ ...editData, active: value === "active" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Observações</Label><Textarea value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} /></div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy/90" disabled={saving}>{saving ? "Salvando..." : "Salvar alterações"}</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showGrant} onOpenChange={setShowGrant}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Liberar acessos extras</DialogTitle></DialogHeader>
            <form onSubmit={handleGrant} className="space-y-4">
              <div>
                <Label>Desconto</Label>
                <Select value={grantData.discount_percent} onValueChange={(value) => setGrantData({ ...grantData, discount_percent: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="100">100%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantidade</Label>
                <Input type="number" min="1" value={grantData.quantity} onChange={(e) => setGrantData({ ...grantData, quantity: e.target.value })} required />
              </div>
              <div>
                <Label>Observações</Label>
                <Textarea value={grantData.notes} onChange={(e) => setGrantData({ ...grantData, notes: e.target.value })} />
              </div>
              <Button type="submit" className="w-full bg-navy hover:bg-navy/90" disabled={granting}>
                {granting ? "Liberando..." : "Confirmar liberação"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showView} onOpenChange={setShowView}>
          <DialogContent className="max-w-6xl">
            <DialogHeader><DialogTitle>Detalhes do contador parceiro</DialogTitle></DialogHeader>
            {dashboardData ? (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <Card><CardContent className="p-4"><p className="text-xs text-slate-500">100% restantes</p><p className="mt-2 text-xl font-semibold text-emerald-600">{dashboardData.quota_summary?.free_remaining || 0}</p></CardContent></Card>
                  <Card><CardContent className="p-4"><p className="text-xs text-slate-500">50% restantes</p><p className="mt-2 text-xl font-semibold text-amber-600">{dashboardData.quota_summary?.half_remaining || 0}</p></CardContent></Card>
                  <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Links ativos</p><p className="mt-2 text-xl font-semibold text-navy">{dashboardData.quota_summary?.active_links || 0}</p></CardContent></Card>
                  <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Usados</p><p className="mt-2 text-xl font-semibold text-sky-600">{dashboardData.quota_summary?.used_links || 0}</p></CardContent></Card>
                </div>

                <div className="rounded-lg border overflow-x-auto">
                  <table className="w-full min-w-[900px] text-sm">
                    <thead className="bg-slate-50 text-left text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Cliente</th>
                        <th className="px-4 py-3">Benefício</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Criado</th>
                        <th className="px-4 py-3">Empresa</th>
                        <th className="px-4 py-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboardData.links || []).map((link) => (
                        <tr key={link.partnership_link_id} className="border-t">
                          <td className="px-4 py-3">
                            <div>{link.client_name || "-"}</div>
                            <div className="text-xs text-slate-500">{link.client_email || "-"}</div>
                          </td>
                          <td className="px-4 py-3">{Number(link.discount_percent || 0) > 0 ? `${link.discount_percent}%` : "Sem desconto"}</td>
                          <td className="px-4 py-3">{link.status}</td>
                          <td className="px-4 py-3">{link.created_at ? new Date(link.created_at).toLocaleString("pt-BR") : "-"}</td>
                          <td className="px-4 py-3">{link.assigned_company_name || "-"}</td>
                          <td className="px-4 py-3">
                            {link.status !== "used" && (
                              <Button size="sm" variant="outline" onClick={() => cancelLink(link)}>
                                Cancelar
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-lg border overflow-x-auto">
                  <table className="w-full min-w-[1100px] text-sm">
                    <thead className="bg-slate-50 text-left text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Cliente</th>
                        <th className="px-4 py-3">Empresa</th>
                        <th className="px-4 py-3">Etapa</th>
                        <th className="px-4 py-3">Benefício</th>
                        <th className="px-4 py-3">Pagamento</th>
                        <th className="px-4 py-3">Criado</th>
                        <th className="px-4 py-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboardData.client_journeys || []).map((journey) => (
                        <tr key={`journey-${journey.partnership_link_id}`} className="border-t">
                          <td className="px-4 py-3">
                            <div>{journey.client_name}</div>
                            <div className="text-xs text-slate-500">{journey.client_email}</div>
                          </td>
                          <td className="px-4 py-3">{journey.company_name}</td>
                          <td className="px-4 py-3">
                            <Badge className={stageBadgeClass(journey.stage_key)}>{journey.stage_label}</Badge>
                          </td>
                          <td className="px-4 py-3">{Number(journey.discount_percent || 0) > 0 ? `${journey.discount_percent}%` : "Sem desconto"}</td>
                          <td className="px-4 py-3">{journey.payment_status_label || "-"}</td>
                          <td className="px-4 py-3">{journey.created_at ? new Date(journey.created_at).toLocaleString("pt-BR") : "-"}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              {journey.diagnostic_id ? (
                                <>
                                  <Link to={`/diagnostic/${journey.diagnostic_id}`}>
                                    <Button size="sm" variant="outline">Diagnóstico</Button>
                                  </Link>
                                  <Link to={`/payment/diagnostic/${journey.diagnostic_id}`}>
                                    <Button size="sm" variant="outline">Pagamento</Button>
                                  </Link>
                                  {(journey.stage_key === "diagnostic_completed" || journey.stage_key.startsWith("consultancy_")) && (
                                    <Link to={`/results/${journey.diagnostic_id}`}>
                                      <Button size="sm" variant="outline">Resultado</Button>
                                    </Link>
                                  )}
                                </>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(journey.public_url || "")}>
                                  Copiar link
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAccountants;
